import { useCallback, useEffect, useMemo, useState } from "react";
import type { TransactionBackendStatus } from "@types";

export interface BatchSelectionItem {
  id: string;
  version?: number | null;
  recurrenceId?: string | null;
  status: TransactionBackendStatus;
  amount: number;
  date?: string | null;
  category?: string | null;
  subcategory?: string | null;
  totalInstallments?: number | null;
  deletable?: boolean;
}

export type BatchSelectionSource = "list" | "filter";

interface SelectionState {
  items: Map<string, BatchSelectionItem>;
  source: BatchSelectionSource;
}

const EMPTY_SELECTION: SelectionState = { items: new Map(), source: "list" };

export const useBatchSelection = (resetKey: string) => {
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);

  const clear = useCallback(() => setSelection(EMPTY_SELECTION), []);

  useEffect(() => clear(), [resetKey, clear]);

  const toggle = useCallback((item: BatchSelectionItem) => {
    setSelection((prev) => {
      const items = new Map(prev.items);
      if (items.has(item.id)) items.delete(item.id);
      else items.set(item.id, item);
      return { items, source: "list" };
    });
  }, []);

  const setMany = useCallback((items: BatchSelectionItem[], checked: boolean) => {
    setSelection((prev) => {
      const next = new Map(prev.items);
      items.forEach((item) => (checked ? next.set(item.id, item) : next.delete(item.id)));
      return { items: next, source: "list" };
    });
  }, []);

  const replaceWithFilter = useCallback((items: BatchSelectionItem[]) => {
    setSelection({ items: new Map(items.map((item) => [item.id, item])), source: "filter" });
  }, []);

  const isSelected = useCallback((id: string) => selection.items.has(id), [selection]);

  const selectedItems = useMemo(() => Array.from(selection.items.values()), [selection]);

  const totalAmount = useMemo(() => selectedItems.reduce((sum, item) => sum + (item.amount || 0), 0), [selectedItems]);

  return {
    selectedItems,
    count: selectedItems.length,
    totalAmount,
    source: selection.source,
    isSelected,
    toggle,
    setMany,
    replaceWithFilter,
    clear,
  };
};

export type BatchSelection = ReturnType<typeof useBatchSelection>;
